/* eslint-disable @typescript-eslint/no-explicit-any */
import { FORM_ERROR } from 'final-form';
import { createAsyncThunk, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { Crypto } from '@vk-hr-tek/core/crypto';
import {
  showNotification,
  showError,
  showCustomNotification,
} from '@vk-hr-tek/core/notifications';
import { CryptoType } from '@vk-hr-tek/core/crypto/types';

import {
  BatchEventOperationsInfoResponse,
  CancelReasonItem,
  EventListRequest,
  EventListRequestFilters,
  EventsService,
} from '@app/gen/events';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';

import {
  GetHashesHashDto,
  SendBatchEventsDto,
  CancelBatchEventsDto,
  GetEventsDto,
} from '../../dto';
import { EventsActionsService } from '../../services';
import { EventsActionsState } from '../events-actions.state';

export const getBatchOperationsInfo = createAsyncThunk<
  BatchEventOperationsInfoResponse,
  EventListRequest,
  ThunkExtra
>(
  'events/getBatchOperationsInfo',
  async (getEventsDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventService = inject(EventsService);
      const { filters, pagination } = getEventsDto;
      const result = await eventService.getBatchEventOperationsInfo({
        body: {
          ...(filters && { filters }),
          pagination,
        },
      });
      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const signUkepBatch = createAsyncThunk<
  void,
  {
    values: {
      data: {
        event_id: string;
        document_id: string;
      }[];
      tsp?: string;
      certificateId: string;
      cryptoType?: CryptoType;
      filters?: EventListRequestFilters;
      isBatch: boolean;
    };
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: { [FORM_ERROR]: AppError }) => void;
    };
  },
  ThunkExtra
>(
  'events/signUkepBatch',
  async ({ values, actions }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = await inject(EventsActionsService);
      const eventService = await inject(EventsService);
      const crypto = await inject(Crypto);
      let eventIds: GetHashesHashDto[] = [];

      if (values.isBatch) {
        const { filters } = values;
        const response = await eventService.ukepListEvents({
          body: filters ? { filters } : {},
        });

        eventIds = (response?.ids ?? []).map((id) => ({ event_id: id }));
      } else {
        eventIds = values.data.map((event) => ({ event_id: event.event_id }));
      }

      const [{ hashes }, certificate] = await Promise.all([
        service
          .getHashes({
            hashes: eventIds,
          })
          .catch(() => {
            throw new Error('Ошибка получения хэша документа');
          }),
        crypto.getSertificateById(values.certificateId, values.cryptoType),
      ]);

      const dataWithSignatures = await Promise.all(
        hashes.map(async (hash) => {
          return {
            ...hash,
            signature: await crypto
              .signHash(certificate, hash.hash, values?.tsp)
              .catch((err) => {
                throw new AppError('client', {
                  code: 500,
                  message: err?.message ?? 'Ошибка при подписании',
                  data: {
                    error_code: values?.tsp ? 'sign_hash_failed' : '',
                  },
                });
              }),
          };
        }),
      );

      interface CompanyBatchSignRequest {
        [key: string]: {
          event_id: string;
          documents: {
            signature: string;
            document_hash: string;
          }[];
        };
      }

      const transformedData = dataWithSignatures.reduce(
        (acc: CompanyBatchSignRequest, item) => {
          acc[item.event_id] = {
            event_id: item.event_id,
            documents: [
              ...((acc[item.event_id] && acc[item.event_id].documents) || []),
              {
                signature: item.signature,
                document_hash: item.hash,
              },
            ],
          };
          return acc;
        },
        {},
      );

      try {
        await eventService.doBatchUkepSign({
          body: { events: Object.values(transformedData) },
        });
      } catch (error) {
        throw new Error('Ошибка отправки подписи');
      }

      actions.resolve(null);
    } catch (err) {
      if (err instanceof AppError) {
        actions.reject({ [FORM_ERROR]: err });

        return rejectWithValue(classToPlain(err) as AppError);
      }

      const appError = new AppError('client', {
        code: 500,
        message: 'Упс! Что-то пошло не так.',
      });

      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      actions.reject({ [FORM_ERROR]: appError });

      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);

export const sendBatchConfirm = createAsyncThunk<
  undefined,
  ThunkValuesAndActions<SendBatchEventsDto>,
  ThunkExtra
>(
  'events/sendBatchConfirm',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const { filters, totalCount, ...body } = values;
      const eventService = inject(EventsService);

      let result;
      if (totalCount) {
        result = await eventService.operationEvents({
          body: {
            ...(filters && { filters }),
            operation: 'batch_accept',
          },
        });
      } else {
        result = await eventService.doBatchAccept({ body });
      }

      if (!result) return;

      const { successes, errors } = result;

      const successCount = successes?.length;
      const errorCount = errors?.length;
      const partial = successCount && errorCount;

      const successName =
        values.events.find((el) => el.event_id === successes?.[0])?.name || '';
      const errorName =
        values.events.find((el) => el.event_id === errors?.[0])?.name || '';

      if (successCount) {
        if (successCount > 1) {
          dispatch(
            showNotification(
              `${
                partial ? 'Часть заявок подтверждена' : 'Заявки подтверждены'
              } (${successCount})`,
            ),
          );
        } else {
          dispatch(
            showCustomNotification([
              {
                type: 'info',
                title: 'Заявка подтверждена',
                message: successName,
              },
            ]),
          );
        }
      }

      if (errorCount) {
        if (errorCount > 1) {
          dispatch(
            showError(
              `Не удалось подтвердить ${
                partial ? 'часть заявок' : 'заявки'
              } (${errorCount})\nПопробуйте еще раз позже`,
            ),
          );
        } else {
          dispatch(
            showCustomNotification([
              {
                type: 'error',
                title:
                  'Не удалось подтвердить заявку\nПопробуйте еще раз позже',
                message: errorName,
              },
            ]),
          );
        }
      }

      if (!successCount && !errorCount) {
        if (values.events.length > 1) {
          dispatch(
            showNotification(
              `Заявки подтверждены (${totalCount || values.events.length})`,
            ),
          );
        } else {
          dispatch(
            showCustomNotification([
              {
                type: 'info',
                title: 'Заявка подтверждена',
                message: successName,
              },
            ]),
          );
        }
      }

      actions.resolve(null);
    } catch (err) {
      actions.reject(null);

      if (values.events.length > 1) {
        dispatch(
          showError(
            `Не удалось подтвердить заявки (${
              values.totalCount || values.events.length
            })\nПопробуйте еще раз позже`,
          ),
        );
      } else {
        dispatch(
          showCustomNotification([
            {
              type: 'error',
              title: 'Не удалось подтвердить заявку\nПопробуйте еще раз позже',
              message: values.events[0].name,
            },
          ]),
        );
      }

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCancelReasons = createAsyncThunk<
  CancelReasonItem[],
  { isCandidate?: boolean } | undefined,
  ThunkExtra
>(
  'events/getCancelReasons',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventService = inject(EventsActionsService);
      const result = await eventService.getCancelReasons({
        isCandidate: params?.isCandidate,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const sendBatchCancel = createAsyncThunk<
  undefined,
  ThunkValuesAndActions<CancelBatchEventsDto>,
  ThunkExtra
>(
  'events/sendBatchCancel',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const { filters, totalCount, ...body } = values;
      const eventListService = inject(EventsActionsService);
      const eventService = inject(EventsService);

      let result;
      if (totalCount) {
        result = await eventService.operationEvents({
          body: {
            ...(filters && { filters }),
            cancel_payload: {
              reason_id: body.reasonId,
              comment: body?.comment,
            },
            operation: 'batch_cancel',
          },
        });
      } else {
        result = await eventListService.sendBatchCancel(body);
      }

      if (!result) return;

      const { successes, errors } = result;

      const successCount = successes?.length;
      const errorCount = errors?.length;
      const partial = successCount && errorCount;

      if (successCount) {
        if (successCount > 1) {
          dispatch(
            showNotification(
              `${
                partial ? 'Часть заявок отменена' : 'Заявки отменены'
              } (${successCount})`,
            ),
          );
        } else {
          dispatch(showNotification('Заявка отменена'));
        }
      }

      if (errorCount) {
        if (errorCount > 1) {
          dispatch(
            showError(
              `Не удалось отменить ${
                partial ? 'часть заявок' : 'заявки'
              } (${errorCount})\nПопробуйте еще раз позже`,
            ),
          );
        } else {
          dispatch(
            showError('Не удалось отменить заявку\nПопробуйте еще раз позже'),
          );
        }
      }

      if (!successCount && !errorCount) {
        if (values.events.length > 1) {
          dispatch(
            showNotification(
              `Заявки отменены (${totalCount || values.events.length})`,
            ),
          );
        } else {
          dispatch(showNotification('Заявка отменена'));
        }
      }
      actions.resolve(null);
    } catch (err) {
      actions.reject(null);

      if (values.events.length > 1) {
        dispatch(
          showError(
            `Не удалось отменить заявки (${
              values.totalCount || values.events.length
            })\nПопробуйте еще раз позже`,
          ),
        );
      } else {
        dispatch(
          showError('Не удалось отменить заявку\nПопробуйте еще раз позже'),
        );
      }

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getDocumentCount = createAsyncThunk<
  {
    totalCount: number;
  },
  GetEventsDto,
  ThunkExtra
>(
  'events/getDocumentCount',
  async (getEventsDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventsService = inject(EventsActionsService);

      return await eventsService.getDocumentCount(getEventsDto);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const eventsReducers = (
  builder: ActionReducerMapBuilder<EventsActionsState>,
) => {
  builder.addCase(getBatchOperationsInfo.pending, (state) => {
    state.batchTotal = null;
  });
  builder.addCase(getBatchOperationsInfo.fulfilled, (state, { payload }) => {
    state.batchTotal = {
      acceptCount: payload?.total_count_to_accept || 0,
      cancelCount: payload?.total_count_to_cancel || 0,
      signCount: payload?.total_count_to_sign_ukep || 0,
      downloadCount: payload?.total_count_to_download || 0,
      batchLimit: payload?.batch_operations_limit || 0,
      batchUkepLimit: payload?.batch_ukep_sign_limit || 0,
      hasOptionalAttrs: !!payload?.optional_attrs_exist,
    };
  });

  builder.addCase(getCancelReasons.pending, (state) => {
    state.cancelReasons = [];
  });
  builder.addCase(getCancelReasons.fulfilled, (state, { payload }) => {
    state.cancelReasons = payload;
  });

  builder.addCase(getDocumentCount.pending, (state) => {
    state.totalDocuments = 0;
  });
  builder.addCase(getDocumentCount.fulfilled, (state, { payload }) => {
    state.totalDocuments = payload.totalCount;
  });
};
